import { useState } from 'react'
import { Info } from 'lucide-react'
import {
  DateValue, EmptyState, ErrorState, PageHeader, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import { useCampaignAcknowledgements, useLibraryMutations, usePolicyCampaigns } from '../api'
import { ACKNOWLEDGEMENT_STATUS_LABELS, CAMPAIGN_STATUS_LABELS } from '../types'
import type { AcknowledgementStatus, CampaignStatus } from '../types'

/** Who was asked about which version, and what they said. */
export function PolicyCampaignsPage() {
  useBreadcrumb()
  const campaigns = usePolicyCampaigns()
  const { closeCampaign } = useLibraryMutations()

  const [selected, setSelected] = useState<string | null>(null)
  const answers = useCampaignAcknowledgements(selected ?? undefined)
  const chosen = campaigns.data?.find((row) => row.id === selected)
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="space-y-4">
      <PageHeader
        title="Policy acknowledgements"
        subtitle="Who was asked about which version, and what they said."
      />

      {error && (
        <p className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          {error}
        </p>
      )}

      <p className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
        <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        Coverage counts the version, not the policy. Publishing a new version does not carry
        the old answers forward — "everybody has acknowledged the handbook" and "everybody has
        acknowledged the handbook they were given" are different sentences, and only the second
        is worth anything in a dispute.
      </p>

      <Card>
        {campaigns.isPending && <Skeleton className="m-4 h-20" />}
        {campaigns.isError && (
          <ErrorState error={campaigns.error} onRetry={() => void campaigns.refetch()} />
        )}
        {campaigns.isSuccess && campaigns.data.length === 0 && (
          <EmptyState
            title="Nobody has been asked to acknowledge anything"
            description="Publish a policy version, then open a campaign from its page. The people in scope are written down when it opens."
          />
        )}
        {campaigns.isSuccess && campaigns.data.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Campaign</th>
                  <th className="p-3 text-start font-medium">Policy</th>
                  <th className="p-3 text-end font-medium">Asked</th>
                  <th className="p-3 text-end font-medium">Acknowledged</th>
                  <th className="p-3 text-end font-medium">Not answered</th>
                  <th className="p-3 text-start font-medium">Due</th>
                  <th className="p-3 text-start font-medium">Status</th>
                  <th className="p-3 text-end font-medium" />
                </tr>
              </thead>
              <tbody>
                {campaigns.data.map((campaign) => (
                  <tr key={campaign.id} className="border-b border-slate-100 align-top">
                    <td className="p-3">
                      <button
                        type="button"
                        className="underline underline-offset-2"
                        onClick={() => setSelected(campaign.id)}
                      >
                        {campaign.name}
                      </button>
                    </td>
                    <td className="p-3 text-slate-600">
                      {campaign.documentTitle} · v{campaign.versionNo}
                    </td>
                    <td className="p-3 text-end">{campaign.asked}</td>
                    <td className="p-3 text-end">{campaign.acknowledged}</td>
                    <td className={`p-3 text-end ${campaign.overdue ? 'font-medium text-amber-700' : ''}`}>
                      {campaign.pending}
                    </td>
                    <td className="p-3">
                      {campaign.dueOn ? <DateValue value={campaign.dueOn} /> : 'No date'}
                    </td>
                    <td className="p-3">
                      <StatusBadge
                        status={campaign.status}
                        tone={campaign.status === 'OPEN' ? 'info'
                          : campaign.status === 'CLOSED' ? 'success' : 'neutral'}
                        label={CAMPAIGN_STATUS_LABELS[campaign.status as CampaignStatus]
                          ?? campaign.status}
                      />
                    </td>
                    <td className="p-3 text-end">
                      {campaign.status === 'OPEN' && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            void (async () => {
                              setError(null)
                              try {
                                await closeCampaign.mutateAsync(campaign.id)
                              } catch (caught) {
                                setError(toApiProblem(caught)?.detail
                                  ?? 'That campaign could not be closed')
                              }
                            })()
                          }}
                        >
                          Close
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {chosen && (
        <>
          <PageHeader title={chosen.name} subtitle={chosen.coverageNote} />
          <Card>
            {answers.isPending && <Skeleton className="m-4 h-20" />}
            {answers.isSuccess && answers.data.length === 0 && (
              <EmptyState
                title="Nobody was in scope"
                description="This campaign says nothing about anybody, rather than reporting complete coverage."
              />
            )}
            {answers.isSuccess && answers.data.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-200 text-slate-600">
                    <tr>
                      <th className="p-3 text-start font-medium">Person</th>
                      <th className="p-3 text-start font-medium">Version</th>
                      <th className="p-3 text-start font-medium">Answer</th>
                      <th className="p-3 text-start font-medium">When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {answers.data.slice(0, 200).map((row) => (
                      <tr key={row.id} className="border-b border-slate-100">
                        <td className="p-3">{row.employeeName ?? row.employeeCode}</td>
                        <td className="p-3 font-mono">v{row.versionNo}</td>
                        <td className="p-3">
                          <StatusBadge
                            status={row.status}
                            tone={row.status === 'ACKNOWLEDGED' ? 'success' : 'warning'}
                            label={ACKNOWLEDGEMENT_STATUS_LABELS[
                              row.status as AcknowledgementStatus] ?? row.status}
                          />
                        </td>
                        <td className="p-3">
                          {row.acknowledgedAt
                            ? <DateValue value={row.acknowledgedAt} />
                            : <span className="text-slate-500">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {answers.data.length > 200 && (
                  <p className="p-3 text-xs text-slate-600">
                    Showing 200 of {answers.data.length}. Use the export for the whole list.
                  </p>
                )}
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  )
}
